import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Gift, Building2, Check, HelpCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

type PricingPlan = {
  id: string;
  name: string;
  price: number;
  description: string;
};

export default function Sponsorship() {
  const [step, setStep] = useState<'form' | 'confirm' | 'payment'>('form');
  const [loading, setLoading] = useState(false);
  const [sponsorshipCode, setSponsorshipCode] = useState("");
  const [ptaData, setPtaData] = useState<any>(null);
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    businessName: "",
    sponsoredPlan: "",
    showOnFundraising: false,
    invoiceEmail: "",
  });

  useEffect(() => {
    fetchPricingPlans();
  }, []);

  const fetchPricingPlans = async () => {
    const { data } = await supabase
      .from('pricing_plans')
      .select('*')
      .order('price', { ascending: true });
    
    if (data) {
      setPricingPlans(data);
      if (data.length > 0) {
        setFormData(prev => ({ ...prev, sponsoredPlan: data[0].id }));
      }
    }
  };

  const verifyCode = async () => {
    if (sponsorshipCode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter a 6-digit sponsorship code",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from('ptas')
      .select('*')
      .eq('sponsorship_code', sponsorshipCode)
      .single();

    setLoading(false);

    if (error || !data) {
      toast({
        title: "Invalid Code",
        description: "No PTA found with this sponsorship code",
        variant: "destructive",
      });
      return;
    }

    setPtaData(data);
    setStep('confirm');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedPlan = pricingPlans.find(p => p.id === formData.sponsoredPlan);
    const planPrice = selectedPlan?.price || 0;
    
    // If invoice email is provided, send the invoice
    if (formData.invoiceEmail) {
      try {
        const { error } = await supabase.functions.invoke('send-sponsorship-invoice', {
          body: {
            businessName: formData.businessName,
            contactName: `${formData.firstName} ${formData.lastName}`,
            email: formData.invoiceEmail,
            ptaName: ptaData?.name,
            schoolName: ptaData?.school_name,
            planName: selectedPlan?.name,
            amount: planPrice,
          },
        });

        if (error) {
          console.error('Error sending invoice:', error);
          toast({
            title: "Invoice Error",
            description: "Failed to send invoice email, but your sponsorship is being processed.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error('Error invoking invoice function:', error);
      }
    }
    
    toast({
      title: "Sponsorship Initiated",
      description: `You'll be redirected to complete payment for £${planPrice.toFixed(2)}/year`,
    });

    // Here you would integrate with Stripe to create a payment session
    // and redirect to Stripe checkout
    setStep('payment');
  };

  return (
    <div className="min-h-screen py-16">
      <div className="container max-w-3xl">
        <div className="text-center mb-8">
          <div className="mx-auto w-20 h-20 rounded-full bg-gradient-primary flex items-center justify-center mb-4">
            <Gift className="h-10 w-10 text-primary-foreground" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Sponsor a PTA</h1>
          <p className="text-lg text-muted-foreground">
            Support local schools by sponsoring their PTA membership
          </p>
        </div>

        <Card className="shadow-elevated">
          <CardHeader>
            <CardTitle>How Sponsorship Works</CardTitle>
            <CardDescription>
              Your business can sponsor a PTA's annual membership, helping them access powerful tools 
              to organize events, manage finances, and engage their community.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <div className="text-3xl mb-2">1️⃣</div>
                <p className="text-sm font-medium">Get the 6-digit code from the PTA</p>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <div className="text-3xl mb-2">2️⃣</div>
                <p className="text-sm font-medium">Verify and confirm sponsorship</p>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <div className="text-3xl mb-2">3️⃣</div>
                <p className="text-sm font-medium">Complete payment securely</p>
              </div>
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Sponsorship Benefits
              </h3>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" />
                  Your business name displayed on the PTA dashboard
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" />
                  Optional visibility on the PTA's fundraising page
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" />
                  Support local education and community engagement
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" />
                  Tax-deductible charitable contribution
                </li>
              </ul>
            </div>

            {step === 'form' && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="font-semibold">Enter Sponsorship Details</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        placeholder="John"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        placeholder="Smith"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@business.com"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="businessName">Business Name</Label>
                    <Input
                      id="businessName"
                      value={formData.businessName}
                      onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                      placeholder="Your Business Name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="invoiceEmail">Invoice Email (Optional)</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">This email will only be used for sending your invoice. We won't use it for any other purpose.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="invoiceEmail"
                      type="email"
                      value={formData.invoiceEmail}
                      onChange={(e) => setFormData({ ...formData, invoiceEmail: e.target.value })}
                      placeholder="billing@business.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="plan">Select Plan to Sponsor</Label>
                    <Select
                      value={formData.sponsoredPlan}
                      onValueChange={(value) => setFormData({ ...formData, sponsoredPlan: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {pricingPlans.map((plan) => (
                          <SelectItem key={plan.id} value={plan.id}>
                            {plan.name} Plan - £{plan.price.toFixed(2)}/year
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="code">6-Digit Sponsorship Code</Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Enter the code provided by the PTA
                    </p>
                    <div className="flex justify-center">
                      <InputOTP
                        maxLength={6}
                        value={sponsorshipCode}
                        onChange={(value) => setSponsorshipCode(value)}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={verifyCode}
                  disabled={loading || sponsorshipCode.length !== 6 || !formData.firstName || !formData.lastName || !formData.email || !formData.businessName}
                  className="w-full"
                  size="lg"
                >
                  {loading ? "Verifying..." : "Verify Code & Continue"}
                </Button>
              </div>
            )}

            {step === 'confirm' && ptaData && (
              <div className="space-y-6">
                <div className="bg-accent/50 border border-border rounded-lg p-6">
                  <h3 className="font-semibold text-lg mb-4">Confirm PTA Details</h3>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">PTA Name:</span> {ptaData.name}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">School:</span> {ptaData.school_name}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Plan:</span> {pricingPlans.find(p => p.id === formData.sponsoredPlan)?.name}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Amount:</span> £{pricingPlans.find(p => p.id === formData.sponsoredPlan)?.price.toFixed(2)}/year
                    </p>
                  </div>
                </div>

                <div className="bg-muted/30 border border-border rounded-lg p-4">
                  <p className="text-sm font-medium mb-2">Your business will be displayed as:</p>
                  <p className="text-lg font-bold text-primary">
                    Membership Sponsored by: {formData.businessName}
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="showOnFundraising"
                    checked={formData.showOnFundraising}
                    onChange={(e) => setFormData({ ...formData, showOnFundraising: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="showOnFundraising" className="text-sm">
                    Also show my business on the PTA's fundraising page
                  </Label>
                </div>

                <p className="text-sm text-muted-foreground text-center">
                  Is this the correct PTA you want to sponsor?
                </p>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setStep('form');
                      setPtaData(null);
                    }}
                    className="flex-1"
                  >
                    No, Go Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    className="flex-1"
                    size="lg"
                  >
                    Yes, Continue to Payment
                  </Button>
                </div>
              </div>
            )}

            {step === 'payment' && (
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg">Redirecting to Payment...</h3>
                <p className="text-sm text-muted-foreground">
                  You'll be redirected to our secure payment page to complete your sponsorship.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
